package uk.co.nickthecoder.kyd.appearance

import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.util.Matrix
import uk.co.nickthecoder.kyd.Actor
import uk.co.nickthecoder.kyd.Behaviour
import uk.co.nickthecoder.kyd.Ticks
import uk.co.nickthecoder.kyd.util.Vector2

/**
 * An animation consisting of a number of still-images.
 * By quickly viewing each of the still-images in sequence an animation appears.
 * The duration of each page is not constant (i.e. on [FlipBookPage] can be visible longer than others).
 *
 * Events can be fired. [onFinished] is fired when the entire flip-book animation is complete.
 * (this will never happen if [repeat] == `true`).
 * When each [FlipBookPage] is finished, [FlipBookPage.onPageFinished] is fired.
 *
 * NOTE. This is a mutable class. However, [FlipBookAnimation] makes COPIES when it is created.
 * So any changes we make to a [FlipBook] do _NOT_ affect pre-existing [FlipBookAnimation]s.
 */
class FlipBook : WithAppearance {

    val pages = mutableListOf<FlipBookPage>()

    /**
     * How many times does this animation repeat?
     * The default value is 1
     *
     * Note, a value < `1` is somewhat meaningless. It means we have finished before we've started!
     */
    var repetitions: Int = 1

    /**
     * When we finish the last page, we return to the first page.
     * Otherwise, when we get to the last page, we reverse direction
     */
    var pingPong = false

    var onFinished: ((Actor) -> Unit)? = null

    fun forever() {
        repetitions = Int.MAX_VALUE
    }

    fun onFinished(block: (Actor) -> Unit) {
        onFinished = block
    }

    override fun appearanceForActor(actor: Actor) = FlipBookAnimation(this, actor)

    override fun appearance() = FlipBookAnimation(this)

}

/**
 * A page within a [FlipBook]. It has an appearance ([withAppearance]) and a duration in [seconds].
 *
 *
 */
class FlipBookPage(
    /**
     * Q. Each page needs an [Appearance], so why are we using [WithAppearance] instead?
     *
     * A. We can supply a [NinePatch] (which is not an [Appearance]
     * For example, one page of the animation could gradually change color (by changing the actor's tint),
     * or shake (by changing the actor's angle back and forth)...
     *
     * This can be `null`, which means the appearance shouldn't change.
     * It might seem strange to have a page which doesn't change the appearance,
     * until you take [tick] and [onPageFinished] into account.
     * For example, suppose we have a flip-book animation of an actor dying.
     * The first few pages could be him falling to the floor, and the final "pages" don't change the appearance,
     * but just fade out (by changing [Actor.tint]).
     *
     */
    val withAppearance: WithAppearance?,

    /**
     * The duration of this page
     */
    val seconds: Float,

    /**
     * An optional action to take while on this page.
     *
     * This is guaranteed to fire at least once (at the end of the page).
     */
    val tick: ((Actor, Float) -> Unit)? = null,

    /**
     * An optional action to take while on this page. Very similar to [tick],
     * but the Float argument is the progress of this page in the range 0..1.
     *
     * This is guaranteed to fire at the start and end of the page.
     * i.e. with values of 0 and 1.
     */
    val progress: ((Actor, Float) -> Unit)? = null,

    val onPageStarted: ((Actor) -> Unit)? = null,

    /**
     * We can optionally perform an arbitrary action at the end of this page.
     * e.g. play a sound, change attributes of the actor or its behaviour, such as its speed, direction...
     */
    val onPageFinished: ((Actor) -> Unit)? = null
)


/**
 * NOTE. [tick] is _NOT_ called automatically, therefore call this from the [Actor]'s [Behaviour].
 * This is a deliberate deign choice (not an oversight, or laziness). For example, suppose
 * an actor can be `frozen`, then your behaviour will contain something like this :
 *
 *     if (!frozen) {
 *         (actor.appearance as? Ticks)?.tick( ... )
 *     }
 *
 */
class FlipBookAnimation(

    flipBook: FlipBook,
    defaultAppearance: Appearance = NoAppearance

) : Appearance, Ticks {

    constructor(flipBook: FlipBook, actor: Actor) : this(flipBook, actor.appearance) {
        start(actor)
    }

    // Copy of data from FlipBook.
    private val pages: List<FlipBookPage> = if (flipBook.pingPong && flipBook.pages.size > 2) {
        // Add pages n-1 to 1 AGAIN.
        // Now we don't need to worry about ping-ponging, we only ever need to more FORWARDS.
        flipBook.pages.toList() + flipBook.pages.slice(1 until flipBook.pages.size - 1).asReversed()
    } else {
        flipBook.pages.toList()
    }
    var repetitions = flipBook.repetitions
        private set

    private val onFinished = flipBook.onFinished

    private var index = 0

    /**
     * How long till we need to turn to the next page in the [FlipBook].
     */
    var nextPageInSeconds = pages.first().seconds
        private set

    /**
     * The current [FlipBookPage].
     */
    var currentPage: FlipBookPage? = null
        private set

    /**
     * The amount of time the current page has been shown.
     */
    var progressSeconds = 0f

    var currentAppearance: Appearance = pages.first().withAppearance?.appearance() ?: defaultAppearance
        private set

    /**
     * Forwards get/set to the [currentAppearance].
     */
    override var tintIfAvailable: Color
        get() = currentAppearance.tintIfAvailable
        set(value) {
            currentAppearance.tintIfAvailable = value
        }

    private fun startPage(newPage: FlipBookPage, actor: Actor) {
        currentPage = newPage
        progressSeconds = 0f
        newPage.withAppearance?.appearanceForActor(actor)?.let {
            currentAppearance = it
        }
        newPage.onPageStarted?.invoke(actor)
        newPage.progress?.invoke(actor, 0f)
    }

    private fun endPage(oldPage: FlipBookPage, actor: Actor, secondsRemaining: Float) {
        oldPage.tick?.invoke(actor, secondsRemaining)
        oldPage.progress?.invoke(actor, 1f)
        oldPage.onPageFinished?.invoke(actor)
        (currentAppearance as? Ticks)?.tick(actor, secondsRemaining)
    }

    private fun start(actor: Actor): FlipBookPage? {
        return pages.firstOrNull()?.also {
            startPage(it, actor)
        }
    }

    override fun tick(actor: Actor, seconds: Float) {
        // There are three scenarios :
        // A : seconds < nextPageInSeconds (we don't move to the next page)
        //     In this case we can apply the whole of `seconds` to the current page.
        // B : seconds > nextPageInSeconds so we need to turn the page.
        //     However, the remaining time may still longer than the NEXT page, so we need to turn the page AGAIN.
        //     We must ensure that each FlipBookPage.tick is given the appropriate amount of time.
        // C : The animation is finished.

        val nowPage = currentPage ?: start(actor) ?: return

        var secondsToApply = seconds

        while (repetitions > 0 && nextPageInSeconds < secondsToApply) {
            // Option B.
            // We need to apply `nextPageInSeconds` to the CURRENT page, turn the page, and then recalculate
            // how much time we still need to apply.
            endPage(nowPage, actor, nextPageInSeconds)
            secondsToApply -= nextPageInSeconds

            // Find the next page.
            index ++
            if (index >= pages.size) {
                if (-- repetitions > 0) {
                    index = 0
                } else {
                    index --
                    nextPageInSeconds = Float.MAX_VALUE
                    onFinished?.invoke(actor)
                    break
                }
            }

            // Turn the page
            val page = pages[index]
            startPage(page, actor)
            nextPageInSeconds = page.seconds
        }

        // We _may_ have changed pages, but we still have the remaining `secondsToApply` for the current page.

        if (repetitions > 0) {
            progressSeconds += secondsToApply
            currentPage?.progress?.invoke(actor, progressSeconds / nowPage.seconds)
            currentPage?.tick?.invoke(actor, secondsToApply)
            nextPageInSeconds -= secondsToApply
        }

        // If the current appearance is tick-able it should still be ticked, regardless of whether this
        // animation is finished or not.
        (currentAppearance as? Ticks)?.tick(actor, secondsToApply)
    }

    // Forward Appearance methods to the [currentAppearance].

    override fun draw(modelMatrix: Matrix) {
        currentAppearance.draw(modelMatrix)
    }

    override fun containsLocalPoint(localPoint: Vector2): Boolean {
        return currentAppearance.containsLocalPoint(localPoint)
    }

    override fun localCorners(): List<Vector2> {
        return currentAppearance.localCorners()
    }

    override fun localBounds(): Pair<Vector2, Vector2> {
        return currentAppearance.localBounds()
    }

}

/**
 * [FlipBookPage] is immutable, so all attributes must be passed to the constructor,
 * which is very awkward. This makes it much more pleasant.
 */
class FlipBookPageBuilder(val appearance: WithAppearance?, val seconds: Float) {

    var tick: ((Actor, Float) -> Unit)? = null

    var progress: ((Actor, Float) -> Unit)? = null

    var onPageStarted: ((Actor) -> Unit)? = null

    var onPageFinished: ((Actor) -> Unit)? = null

    fun tick(block: (Actor, Float) -> Unit) {
        tick = block
    }

    fun progress(block: (Actor, Float) -> Unit) {
        progress = block
    }

    fun onPageStarted(block: (Actor) -> Unit) {
        onPageStarted = block
    }

    fun onPageFinished(block: (Actor) -> Unit) {
        onPageFinished = block
    }
}

/**
 * Syntactic sugar for creating [FlipBook]s.
 */
fun flipBook(block: FlipBook.() -> Unit): FlipBook {
    return FlipBook().also { block.invoke(it) }
}

/**
 * Syntactic sugar for creating [FlipBookAnimation]s.
 */
fun flipBookAnimation(actor: Actor, block: FlipBook.() -> Unit): FlipBookAnimation {
    return FlipBook().also { block.invoke(it) }.appearanceForActor(actor)
}

/**
 * Syntactic sugar for creating [FlipBookAnimation]s.
 */
fun flipBookAnimation(block: FlipBook.() -> Unit): FlipBookAnimation {
    return FlipBook().also { block.invoke(it) }.appearance()
}

/**
 * Syntactic sugar for adding a [FlipBookPage] to a [FlipBook].
 *
 * Example code :
 *
 *     val game = PlayActView.instance.game
 *     val square = game.find(...) as Appearance
 *     val triangle = game.find(...) as Appearance
 *
 *     val myAnimation = flipBookAnimation(actor) {
 *         // Square for 1 second, rotating (will complete a full turn)
 *         page(square, 1f) {
 *             tick { actor, seconds -> actor.angleDegrees += seconds * 360f }
 *         }
 *         // Pause to 1/2 second
 *         page(null, 0.5f)
 *         // Triangle for 1 second, rotate in the opposite direction
 *         page(triangle, 1f) {
 *             tick { actor, seconds -> actor.angleDegrees -= seconds * 360f }
 *         }
 *         // Pause to 1/2 second
 *         page(null, 0.5f)
 *    }
 *
 * If you want to define a FlipBook for use by many actors, then :
 *
 *     val myFlipBook = flipBook(actor) {
 *         ... (same body as before)
 *     }
 *
 *     myFlipBook.applyToActor( actor1 )
 *     myFlipBook.applyToActor( actor2 )
 *
 */
fun FlipBook.page(appearance: WithAppearance?, seconds: Float, block: (FlipBookPageBuilder.() -> Unit)? = null) {
    val pageDSL = FlipBookPageBuilder(appearance, seconds)
    block?.invoke(pageDSL)

    val page = with(pageDSL) {
        FlipBookPage(
            appearance, seconds,
            tick = tick, progress = progress,
            onPageStarted = onPageStarted, onPageFinished = onPageFinished
        )
    }
    pages.add(page)
}
